import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import Navbar from '../components/navbar';
import { authApi } from '../api/authApi';
import { DEFAULT_CLIENT_CONFIG } from '../api/configApi';
import IClientConfig from '../models/IClientConfig';
import { useAppState } from '../reduxish/appState';

function buildPasswordHint(c: IClientConfig): string {
    const reqs: string[] = [];
    if (c.passwordRequireUpper) reqs.push('uppercase');
    if (c.passwordRequireLower) reqs.push('lowercase');
    if (c.passwordRequireDigit) reqs.push('number');
    if (c.passwordRequireSpecial) reqs.push('special character');
    const reqText = reqs.length > 0 ? ` with ${reqs.join(', ')}` : '';
    return `At least ${c.passwordMinLength} characters${reqText}`;
}

export default function ResetPasswordPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get('token') ?? '';

    const { state } = useAppState();
    const clientConfig = state.clientConfig ?? DEFAULT_CLIENT_CONFIG;

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!newPassword || !confirmPassword) {
            setError('Please fill in both password fields.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }
        if (newPassword.length < clientConfig.passwordMinLength) {
            setError(`Password must be at least ${clientConfig.passwordMinLength} characters long.`);
            return;
        }

        setSubmitting(true);
        try {
            await authApi.resetPassword(token, newPassword);
            setSuccess(true);
            setTimeout(() => navigate('/signin', { replace: true }), 2000);
        } catch (err: any) {
            setError(err?.message || 'Could not reset your password. The link may be invalid or expired.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Navbar />
            <Container maxWidth="sm" sx={{ mt: 8 }}>
                <Paper elevation={3} sx={{ p: 4 }}>
                    <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'center' }}>
                        Choose a new password
                    </Typography>

                    {!token ? (
                        <>
                            <Alert severity="error" sx={{ mb: 2 }}>
                                This reset link is missing its token. Please request a new password reset email.
                            </Alert>
                            <Box sx={{ textAlign: 'center' }}>
                                <Button component={Link} to="/forgot-password" variant="contained">
                                    Request new link
                                </Button>
                            </Box>
                        </>
                    ) : success ? (
                        <>
                            <Alert severity="success" sx={{ mb: 2 }}>
                                Your password has been reset. Redirecting you to sign in…
                            </Alert>
                            <Box sx={{ textAlign: 'center' }}>
                                <Button component={Link} to="/signin" variant="contained">
                                    Continue to Sign In
                                </Button>
                            </Box>
                        </>
                    ) : (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {buildPasswordHint(clientConfig)}.
                            </Typography>

                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            <Box component="form" onSubmit={handleSubmit} noValidate>
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    label="New password"
                                    type="password"
                                    autoComplete="new-password"
                                    autoFocus
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    disabled={submitting}
                                />
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    label="Confirm new password"
                                    type="password"
                                    autoComplete="new-password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    disabled={submitting}
                                />
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    sx={{ mt: 2, mb: 1, py: 1.5 }}
                                    disabled={submitting}
                                >
                                    {submitting ? <CircularProgress size={24} /> : 'Reset password'}
                                </Button>
                            </Box>

                            <Box sx={{ mt: 2, textAlign: 'center' }}>
                                <Typography variant="body2">
                                    <Link to="/signin" style={{ color: 'inherit' }}>
                                        Back to Sign In
                                    </Link>
                                </Typography>
                            </Box>
                        </>
                    )}
                </Paper>
            </Container>
        </>
    );
}
